import { useState, useEffect } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Chip from "@mui/material/Chip";
import axios from "axios";
import moment from "moment";

import MDBox from "admin/components/MDBox";
import MDTypography from "admin/components/MDTypography";
import MDInput from "admin/components/MDInput";
import MDButton from "admin/components/MDButton";
import DashboardLayout from "admin/examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "admin/examples/Navbars/DashboardNavbar";

const API_URL = process.env.REACT_APP_APIHOST || "http://localhost:3033";

function SatelliteAnalysis() {
    const [jobs, setJobs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [form, setForm] = useState({ name: "", address: "", lat: "", lng: "", date: "", description: "", scanType: "area" });
    const [submitting, setSubmitting] = useState(false);
    const [activeJob, setActiveJob] = useState(null);

    const fetchJobs = async () => {
        try {
            const res = await axios.get(API_URL + "/sat-gen");
            if (res.data.status) setJobs(res.data.jobs || []);
        } catch (e) {}
        setLoading(false);
    };

    useEffect(() => { fetchJobs(); }, []);

    // Poll active job
    useEffect(() => {
        if (!activeJob) return;
        const timer = setInterval(async () => {
            try {
                const res = await axios.get(API_URL + "/sat-gen/" + activeJob);
                if (res.data.status && res.data.job) {
                    const j = res.data.job;
                    if (j.status === "complete" || j.status === "failed" || j.status === "error") {
                        setActiveJob(null);
                        fetchJobs();
                    }
                }
            } catch (e) {}
        }, 3000);
        return () => clearInterval(timer);
    }, [activeJob]);

    const handleSubmit = async () => {
        if (!form.name || !form.date || !form.description) return alert("Fill name, date, description");
        if (!form.lat && !form.address) return alert("Provide lat/lng or address");
        setSubmitting(true);
        try {
            const res = await axios.post(API_URL + "/sat-gen", form);
            if (res.data.status) {
                setActiveJob(res.data.jobId);
                setForm({ name: "", address: "", lat: "", lng: "", date: "", description: "", scanType: "area" });
                fetchJobs();
            }
        } catch (e) { alert("Error: " + (e.response?.data?.error || e.message)); }
        setSubmitting(false);
    };

    // Auto-geocode address
    const handleAddressChange = (val) => {
        setForm(f => ({ ...f, address: val }));
        if (val.length < 3 || form.lat) return;
        clearTimeout(window._satGeoTimer);
        window._satGeoTimer = setTimeout(async () => {
            try {
                const res = await fetch("https://nominatim.openstreetmap.org/search?format=json&limit=1&q=" + encodeURIComponent(val));
                const data = await res.json();
                if (data && data.length > 0) {
                    setForm(f => ({ ...f, lat: parseFloat(data[0].lat).toFixed(5), lng: parseFloat(data[0].lon).toFixed(5) }));
                }
            } catch (e) {}
        }, 600);
    };

    const statusColor = (s) => {
        if (s === "complete") return "success";
        if (s === "failed" || s === "error") return "error";
        if (s === "running") return "warning";
        return "default";
    };

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <MDBox pt={3} pb={3}>
                <Grid container spacing={3}>
                    {/* Request form */}
                    <Grid item xs={12} md={5}>
                        <Card>
                            <MDBox p={3}>
                                <MDTypography variant="h6" fontWeight="medium" mb={2} sx={{ color: "#fff !important" }}>
                                    <Icon sx={{ verticalAlign: "middle", mr: 1, color: "#C29717" }}>satellite_alt</Icon>
                                    New satellite analysis
                                </MDTypography>

                                <MDInput type="text" label="Target name" fullWidth value={form.name}
                                    onChange={e => setForm(f => ({ ...f, name: e.target.value }))} sx={{ mb: 2 }} />

                                <MDInput type="text" label="Location address" fullWidth value={form.address}
                                    onChange={e => handleAddressChange(e.target.value)} sx={{ mb: 1 }}
                                    placeholder="Type address, coordinates auto-fill" />

                                <Grid container spacing={1} sx={{ mb: 2 }}>
                                    <Grid item xs={6}>
                                        <MDInput type="text" label="Latitude" fullWidth value={form.lat}
                                            onChange={e => setForm(f => ({ ...f, lat: e.target.value }))} />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <MDInput type="text" label="Longitude" fullWidth value={form.lng}
                                            onChange={e => setForm(f => ({ ...f, lng: e.target.value }))} />
                                    </Grid>
                                </Grid>

                                <Grid container spacing={1} sx={{ mb: 2 }}>
                                    <Grid item xs={6}>
                                        <MDInput type="date" label="Incident date" fullWidth value={form.date}
                                            onChange={e => setForm(f => ({ ...f, date: e.target.value }))}
                                            InputLabelProps={{ shrink: true }} />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <MDBox display="flex" gap={1} pt={0.5}>
                                            {["area", "building"].map(t => (
                                                <MDBox key={t} onClick={() => setForm(f => ({ ...f, scanType: t }))}
                                                    sx={{
                                                        flex: 1, textAlign: "center", py: 1, cursor: "pointer",
                                                        border: form.scanType === t ? "1px solid rgba(194,151,23,0.4)" : "1px solid rgba(255,255,255,0.08)",
                                                        borderRadius: "4px", fontSize: "11px", color: form.scanType === t ? "#C29717" : "#888",
                                                        background: form.scanType === t ? "rgba(194,151,23,0.08)" : "transparent",
                                                    }}>
                                                    {t === "area" ? "Whole area" : "Building"}
                                                </MDBox>
                                            ))}
                                        </MDBox>
                                    </Grid>
                                </Grid>

                                <MDInput type="text" label="Description" fullWidth multiline rows={3} value={form.description}
                                    onChange={e => setForm(f => ({ ...f, description: e.target.value }))} sx={{ mb: 2 }}
                                    placeholder="Describe the incident..." />

                                <MDButton color="warning" fullWidth onClick={handleSubmit} disabled={submitting}>
                                    {submitting ? "Submitting..." : "Generate analysis"}
                                </MDButton>

                                {activeJob && (
                                    <MDBox mt={2} p={1.5} sx={{ border: "1px solid rgba(194,151,23,0.2)", borderRadius: "4px" }}>
                                        <MDTypography variant="caption" sx={{ color: "#C29717 !important" }}>
                                            Generating... Job ID: {activeJob}
                                        </MDTypography>
                                    </MDBox>
                                )}
                            </MDBox>
                        </Card>
                    </Grid>

                    {/* Jobs list */}
                    <Grid item xs={12} md={7}>
                        <Card>
                            <MDBox p={3}>
                                <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                                    <MDTypography variant="h6" fontWeight="medium" sx={{ color: "#fff !important" }}>
                                        <Icon sx={{ verticalAlign: "middle", mr: 1, color: "#C29717" }}>history</Icon>
                                        Previous analyses
                                    </MDTypography>
                                    <MDTypography variant="caption" sx={{ color: "#888 !important" }}>
                                        {jobs.length} total
                                    </MDTypography>
                                </MDBox>

                                {loading && <MDTypography variant="body2" sx={{ color: "#888 !important" }}>Loading...</MDTypography>}

                                {jobs.map((j, i) => (
                                    <MDBox key={j._id || i} sx={{
                                        py: 1.5, borderBottom: "1px solid rgba(255,255,255,0.04)",
                                        display: "flex", justifyContent: "space-between", alignItems: "center",
                                        "&:hover": { background: "rgba(194,151,23,0.03)" },
                                    }}>
                                        <MDBox>
                                            <MDBox display="flex" alignItems="center" gap={1}>
                                                <MDTypography variant="body2" fontWeight="medium" sx={{ color: "#fff !important", fontSize: "13px" }}>
                                                    {j.name}
                                                </MDTypography>
                                                <Chip label={j.status} size="small" color={statusColor(j.status)}
                                                    sx={{ height: "18px", fontSize: "9px", textTransform: "uppercase" }} />
                                                {j.scanType === "building" && (
                                                    <Chip label="building" size="small" variant="outlined"
                                                        sx={{ height: "18px", fontSize: "9px", color: "#888", borderColor: "rgba(255,255,255,0.1)" }} />
                                                )}
                                            </MDBox>
                                            <MDTypography variant="caption" sx={{ color: "#666 !important" }}>
                                                {moment(j.createdAt).format("DD MMM YYYY, HH:mm")} — {(j.lat || 0).toFixed(2)}N, {(j.lng || 0).toFixed(2)}E
                                            </MDTypography>
                                        </MDBox>
                                        <MDBox display="flex" gap={1}>
                                            {j.url && (
                                                <MDButton variant="outlined" size="small" color="warning"
                                                    onClick={() => window.open(j.url, "_blank")}
                                                    sx={{ fontSize: "10px", py: 0.3, px: 1.5, textTransform: "none" }}>
                                                    View
                                                </MDButton>
                                            )}
                                            {j.url && (
                                                <MDButton variant="text" size="small" color="info"
                                                    onClick={() => {
                                                        setForm({ name: j.name, address: j.address || "", lat: j.lat || "", lng: j.lng || "", date: j.date || "", description: j.description || "", scanType: j.scanType || "area" });
                                                    }}
                                                    sx={{ fontSize: "10px", py: 0.3, px: 1, textTransform: "none", minWidth: "auto" }}>
                                                    Reuse
                                                </MDButton>
                                            )}
                                        </MDBox>
                                    </MDBox>
                                ))}

                                {!loading && jobs.length === 0 && (
                                    <MDTypography variant="body2" sx={{ color: "#666 !important", py: 3, textAlign: "center" }}>
                                        No analyses yet. Create one using the form.
                                    </MDTypography>
                                )}
                            </MDBox>
                        </Card>
                    </Grid>
                </Grid>
            </MDBox>
        </DashboardLayout>
    );
}

export default SatelliteAnalysis;
